import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import useOutsideClick from "../../../hooks/useOutsideClick";
import { formatShortMonthDayShortYear } from "../../../utils/datetime";
import { Chip } from "../buttons";
import { Calendar } from "../../icons/svg/outline";
import InlineDatePicker from "./InlineDatePicker";

/**
 * Normalize optional YYYY-MM-DD bounds (swap if inverted).
 *
 * @param {string|null|undefined} start
 * @param {string|null|undefined} end
 * @returns {{start: string|null, end: string|null}}
 */
function normalizeDateBounds(start, end) {
  const startKey = start || null;
  const endKey = end || null;
  if (startKey && endKey && startKey > endKey) {
    return { start: endKey, end: startKey };
  }
  return { start: startKey, end: endKey };
}

/**
 * Single date field with inline calendar popover and optional suggestion chips.
 *
 * @param {Object} props
 * @param {string} props.label - Field label.
 * @param {string} props.value - Current ``YYYY-MM-DD`` (or empty).
 * @param {(next: string) => void} props.onChange - Calendar day selection.
 * @param {string} [props.min] - Inclusive min date key.
 * @param {string} [props.max] - Inclusive max date key.
 * @param {Array<Object>} [props.suggestions=[]] Chip objects with ``label`` + ``value`` and/or ``start``/``end``.
 * @param {string} [props.suggestionsTitle="Suggestions"] Section heading when chips exist.
 * @param {(suggestion: Object) => void} [props.onSelectSuggestion] Chip handler; defaults to ``value``.
 * @param {"left"|"right"} [props.align="left"] Popover horizontal anchor.
 * @param {string} [props.ariaLabel] Accessible name.
 * @param {string} [props.placeholder] Empty-state trigger text.
 * @returns {JSX.Element}
 */
function DateFieldWithSuggestions({
  label,
  value,
  onChange,
  min,
  max,
  suggestions = [],
  suggestionsTitle = "Suggestions",
  onSelectSuggestion,
  align = "left",
  ariaLabel,
  placeholder,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState({
    top: 0,
    left: 0,
    right: 0,
  });
  const rootRef = useRef(null);
  const closePanel = useCallback(() => setIsOpen(false), []);
  useOutsideClick(rootRef, closePanel, {
    ignoreSelector: "[data-date-range-picker-panel]",
  });

  const displayValue = value ? formatShortMonthDayShortYear(value) : "";

  /**
   * Anchor the portaled panel under this field (left or right aligned).
   *
   * @returns {void}
   */
  const updatePanelPosition = useCallback(() => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    setPanelPosition({
      top: rect.bottom + 4,
      left: rect.left,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updatePanelPosition();
    window.addEventListener("scroll", updatePanelPosition, true);
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      window.removeEventListener("scroll", updatePanelPosition, true);
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [isOpen, updatePanelPosition]);

  /**
   * Commit a calendar day and close immediately.
   *
   * @param {string} next - ``YYYY-MM-DD`` or empty.
   * @returns {void}
   */
  const commit = (next) => {
    onChange(next || "");
    setIsOpen(false);
  };

  /**
   * Apply a suggestion chip and close immediately.
   *
   * @param {Object} suggestion
   * @returns {void}
   */
  const selectSuggestion = (suggestion) => {
    if (onSelectSuggestion) {
      onSelectSuggestion(suggestion);
    } else if (suggestion?.value) {
      onChange(suggestion.value);
    }
    setIsOpen(false);
  };

  return (
    <div ref={rootRef} className="relative min-w-[6.5rem]">
      <div className="mb-1 text-xs font-medium font-inter text-gray-600">{label}</div>
      <button
        type="button"
        aria-label={ariaLabel || label}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex h-[34px] w-full min-w-[6.5rem] max-w-[9.5rem] items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 text-left text-sm text-gray-900 shadow-sm outline-none transition hover:border-secondary-300 focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100"
      >
        <Calendar className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden />
        <span
          className={`min-w-0 truncate ${
            displayValue ? "font-medium" : "text-gray-400"
          }`}
        >
          {displayValue || placeholder || label}
        </span>
      </button>

      {isOpen
        ? createPortal(
            <div
              data-date-range-picker-panel
              role="dialog"
              aria-label={ariaLabel || label}
              className="fixed z-[80] w-[17.5rem] overflow-hidden rounded-lg border border-gray-200 bg-white p-3 shadow-lg shadow-primary-900/10"
              style={{
                top: panelPosition.top,
                ...(align === "right"
                  ? { right: panelPosition.right }
                  : { left: panelPosition.left }),
              }}
            >
              <InlineDatePicker
                value={value || ""}
                min={min}
                max={max}
                onChange={commit}
              />

              {suggestions.length > 0 ? (
                <div className="mt-3 border-t border-gray-100 pt-2">
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    {suggestionsTitle}
                  </div>
                  <div className="grid max-h-40 grid-cols-2 gap-1.5 overflow-y-auto">
                    {suggestions.map((suggestion) => (
                      <Chip
                        key={
                          suggestion.key ||
                          suggestion.monthKey ||
                          `${suggestion.label}-${suggestion.value || suggestion.start}`
                        }
                        label={suggestion.label}
                        count={suggestion.count}
                        onClick={() => selectSuggestion(suggestion)}
                        className="justify-center"
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

/**
 * Start/end date fields with inline calendars and optional suggestion chips.
 *
 * Suggestion chips may set one bound via ``value``, or both via ``start`` + ``end``.
 *
 * @param {Object} props
 * @param {{start: string|null, end: string|null}} props.value - Active range.
 * @param {(next: {start: string|null, end: string|null}) => void} props.onChange
 * @param {Array<Object>} [props.startSuggestions=[]] Chips under the start picker.
 * @param {Array<Object>} [props.endSuggestions=[]] Chips under the end picker.
 * @param {string} [props.startSuggestionsTitle="Suggestions"]
 * @param {string} [props.endSuggestionsTitle="Suggestions"]
 * @param {string} [props.startLabel="Start"]
 * @param {string} [props.endLabel="End"]
 * @param {string} [props.ariaLabel="Date range"]
 * @param {string} [props.startAriaLabel="Range start date"]
 * @param {string} [props.endAriaLabel="Range end date"]
 * @returns {JSX.Element}
 */
const DateRangePicker = ({
  value,
  onChange,
  startSuggestions = [],
  endSuggestions = [],
  startSuggestionsTitle = "Suggestions",
  endSuggestionsTitle = "Suggestions",
  startLabel = "Start",
  endLabel = "End",
  ariaLabel = "Date range",
  startAriaLabel = "Range start date",
  endAriaLabel = "Range end date",
}) => {
  const startValue = value?.start || "";
  const endValue = value?.end || "";

  /**
   * Patch one bound and emit normalized ``{ start, end }``.
   *
   * @param {"start"|"end"} bound
   * @param {string} next
   * @returns {void}
   */
  const updateBound = (bound, next) => {
    const start = bound === "start" ? next || null : startValue || null;
    let end = bound === "end" ? next || null : endValue || null;
    if (bound === "start" && start && end && start > end) {
      end = null;
    }
    onChange(normalizeDateBounds(start, end));
  };

  /**
   * Apply a suggestion: full range when both bounds exist, else one bound.
   *
   * @param {"start"|"end"} bound
   * @param {Object} suggestion
   * @returns {void}
   */
  const applySuggestion = (bound, suggestion) => {
    if (suggestion?.start != null && suggestion?.end != null) {
      onChange(normalizeDateBounds(suggestion.start, suggestion.end));
      return;
    }
    if (suggestion?.value) {
      updateBound(bound, suggestion.value);
    }
  };

  return (
    <div
      className="flex shrink-0 items-end gap-1"
      role="group"
      aria-label={ariaLabel}
    >
      <DateFieldWithSuggestions
        label={startLabel}
        value={startValue}
        onChange={(next) => updateBound("start", next)}
        max={endValue || undefined}
        suggestions={startSuggestions}
        suggestionsTitle={startSuggestionsTitle}
        onSelectSuggestion={(suggestion) =>
          applySuggestion("start", suggestion)
        }
        align="right"
        ariaLabel={startAriaLabel}
      />
      <DateFieldWithSuggestions
        label={endLabel}
        value={endValue}
        onChange={(next) => updateBound("end", next)}
        min={startValue || undefined}
        suggestions={endSuggestions}
        suggestionsTitle={endSuggestionsTitle}
        onSelectSuggestion={(suggestion) => applySuggestion("end", suggestion)}
        align="right"
        ariaLabel={endAriaLabel}
      />
    </div>
  );
};

export default DateRangePicker;
